/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {evaluateFeatureFlags} from '@/feature-flags/evaluate-feature-flags';
import Experiment from '@/feature-flags/experiment';
import {withIgData} from '@/feature-flags/with-ig-data';
import {
  getCustomerPrivacy,
  getTrackingConsent,
} from '@/privacy/getTrackingConsent';
import {initializeRecharge} from '@/recharge-storefront/client';
import {logError, logInfo} from '@/utilities/log';
import {datadogRum} from '@datadog/browser-rum';
import {kebabCase} from 'change-case';
import datadogConfig from '~/datadog.config';

Experiment.initGlobal(window);

const shopDomain = import.meta.env.VITE_PUBLIC_STORE_DOMAIN as string;
const rechargeStorefrontToken = import.meta.env
  .VITE_RECHARGE_STOREFRONT_API_TOKEN as string;

/**
 * This entrypoint is for code that MUST execute in the head of every page on the site.
 * This should be limited to things that are required to run before the rest of the page is loaded.
 *
 * If you need to run code on every page, but it doesn't need to run in the head, use the
 * `src/entrypoints/main.tsx` entrypoint instead.
 */

handleInitDatadog();
handleInitRecharge().catch(logError);
handleInitIntelligems();

// Load feature flags from cart attributes and URL parameters
evaluateFeatureFlags().catch(logError);

/**
 * Initializes Datadog RUM and logs any errors that occur.
 * This function also sets the tracking consent status for Datadog,
 * and listens for changes to the tracking consent status.
 */
function handleInitDatadog() {
  try {
    const customerPrivacy = getCustomerPrivacy();

    // Uncomment this line to enable tracking consent:
    // const needsConsent = isConsentRequired(customerPrivacy);
    const needsConsent = false;

    if (needsConsent) {
      datadogRum.init({
        ...datadogConfig,
        trackingConsent: getTrackingConsent(customerPrivacy),
      });

      document.addEventListener('visitorConsentCollected', () => {
        datadogRum.setTrackingConsent(getTrackingConsent(customerPrivacy));
      });
    } else {
      datadogRum.init(datadogConfig);
    }
  } catch (error) {
    logError(error, {message: 'ErrorInitializingDatadog'});
  }
}

/**
 * Initializes Recharge storefront client and logs any errors that occur.
 */
async function handleInitRecharge() {
  const options = {
    storeIdentifier: shopDomain,
    storefrontAccessToken: rechargeStorefrontToken,
  };
  try {
    if (!shopDomain) {
      throw new Error('Missing VITE_PUBLIC_STORE_DOMAIN variable');
    }

    if (!rechargeStorefrontToken) {
      throw new Error('Missing VITE_RECHARGE_STOREFRONT_API_TOKEN variable');
    }

    await initializeRecharge(options);

    logInfo('InitializedRecharge', options);
  } catch (error) {
    logError(error, {message: 'ErrorInitializingRecharge', ...options});
  }
}

/**
 * Wait for intelligems JS API to be ready,
 * then log the experiments and test groups the user is in.
 * Each experiment and test group is sent to Datadog as a feature flag evaluation.
 */
function handleInitIntelligems() {
  withIgData(({user}) => {
    const experiments = user.getExperiments().map((experiment) => ({
      experiment,
      testGroup: user.getTestGroup(experiment.id),
    }));
    for (const {experiment, testGroup} of experiments) {
      if (!testGroup) continue;
      datadogRum.addFeatureFlagEvaluation(
        `ig-${kebabCase(experiment.name)}`,
        !testGroup.isControl
      );
    }
    logInfo('IntelligemsData', {
      intelligemsExperiments: experiments,
    });
  });
}
